import {
    type DateRange,
    type DocumentTagDTO,
    type EmployeeSortDirection,
    type EmployeeSortProperty,
    type EntityFilter,
    ExportFormat,
    ExportParams,
    type ExportResponse,
    dateAdd,
    deserializeColumOptions,
    employeeDisplayProperties,
    filterForPermissions,
    formatMonthShort,
    getPayrollPeriod,
    getRange,
    toDateString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../components/documents/document-tags-input";
import type { DocumentTagsInput } from "../../components/documents/document-tags-input";
import { alert } from "../../elements/alert-dialog";
import { Checkbox } from "../../elements/checkbox";
import "../../elements/column-selector";
import type { ColumnSelector } from "../../elements/column-selector";
import { DateInput } from "../../elements/date-input";
import "../../elements/date-range-picker";
import type { DateRangePicker } from "../../elements/date-range-picker";
import "../../elements/entity-filters";
import type { EntityFiltersEl } from "../../elements/entity-filters";
import "../../elements/popover";
import "../../elements/scroller";
import type { Scroller } from "../../elements/scroller";
import { app } from "../../init";
import { canAccess } from "../../lib/route-tree";
import { type RouteArgs, Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { exportFormat, exportsPage, exportsPages, exportsRoute, isExportFormat } from "../app-routes";
import { routeAccess } from "../route-access";
import "./components/exports-headers";
import "./components/exports-menu";
import "./components/exports-result";

function parseExportResolution(value: string | null): string {
    //"day" and "none" are not allowed here
    if (value === "week" || value === "year") {
        return value;
    }

    return "month";
}

@customElement("ptc-exports")
export class Exports extends Routing(StateMixin(LitElement)) {
    routePattern = /^exports(?:\/(?<page>[\w-]+))?/;

    defaultRange = () => {
        return getPayrollPeriod(toDateString(new Date()), app.company.settings.startPayrollPeriod);
    };

    get helpPage() {
        return "/handbuch/datenexport/";
    }

    @state()
    private _loading: boolean = false;

    @state()
    private _exportResponse: ExportResponse | null = null;

    @routeProperty({ arg: "page", parse: exportFormat })
    private _page: ExportFormat;

    @routeProperty({ param: "resolution", parse: parseExportResolution })
    private _resolutionParam: string;

    @query("ptc-entity-filters")
    private _filtersEl: EntityFiltersEl;

    @query("ptc-column-selector")
    private _columnSelector: ColumnSelector;

    @query("ptc-document-tags-input")
    private _tagsInput: DocumentTagsInput;

    @query("ptc-scroller")
    private _scroller: Scroller;

    @query("ptc-date-range-picker")
    private _dateRangePicker: DateRangePicker;

    @query("form")
    private _exportForm: HTMLFormElement;

    @state()
    private _availableTags?: DocumentTagDTO[] = undefined;

    private get _exportFormData(): Partial<ExportParams> {
        const data = this._exportForm && new FormData(this._exportForm);

        if (!data) {
            return {};
        }
        return {
            //FIXME: This is unsafe usage of the API, as it is expecting defined 'from' and 'to' values
            ...this._dateRangePicker?.range,
            resolution: (data.get("resolution") || undefined) as ExportParams["resolution"],
            filters: this._filtersEl?.filters || [],
            columns:
                this._page === ExportFormat.EmployeeTableExcel
                    ? this._columnSelector?.displayColumns.filter((c) => !c.disabled) || []
                    : [],
            sendAsEmail: data.get("sendAsEmail") ? [data.get("sendAsEmail") as string] : undefined,
            saveToDocuments: data.has("saveToDocuments"),
            showBreakDetails: data.has("showBreakDetails"),
            requireEmployeeSignature: data.has("requireEmployeeSignature"),
            venueId: data.get("venueId") ? Number(data.get("venueId") as string) : undefined,
            tags: this._tagsInput?.tags,
            sortProperty: data.get("sortProperty") as EmployeeSortProperty,
            sortDirection: data.get("sortDirection") as EmployeeSortDirection,
        };
    }

    async handleRoute(_args: RouteArgs, params: { columns: string }) {
        if (!isExportFormat(this._page) || !this._hasPermission(this._page)) {
            const page = exportsPages.find((page) => canAccess(exportsRoute(page), routeAccess()));
            this.go(`exports/${page}`, undefined, true);
            return;
        }

        this._exportResponse = null;

        if (this._allowsExportToDocuments() && this._availableTags === undefined) {
            try {
                this._availableTags = await app.api.getDocumentTagsForCompany();
            } catch (error) {
                console.error("Failed to load document tags:", error);
                //do not set available tags if failed, so that we do not mask the error
            }
        }

        this.requestUpdate();
        await this.updateComplete;

        let filters = this.entityFiltersJSON ? (JSON.parse(this.entityFiltersJSON) as EntityFilter[]) : [];

        if (this._filtersEl && filters.length) {
            if (this._page.startsWith("payroll") || this._page.startsWith("time")) {
                filters = filters.filter((f) => f.type !== "employeeStatus");
            }
            this._filtersEl.filters = filters;
            this.go(null, { ...this.router.params, "entity-filters": undefined });
            return;
        }

        // Set to all columns by default, enable only the ones that were passed as enabled in route
        const columns = params.columns && deserializeColumOptions(params.columns);
        if (this._columnSelector && columns) {
            const enabledColumnTypes = new Set(columns.filter((c) => !c.disabled).map((c) => c.type));
            const newColumns = employeeDisplayProperties.map((type) => ({
                type,
                disabled: !enabledColumnTypes.has(type),
            }));
            const filteredColumns = filterForPermissions((permission) => app.hasPermission(permission), newColumns);
            this._columnSelector.columns = filteredColumns;

            this.go(null, { ...this.router.params, columns: undefined }, true);
            return;
        }

        if (this._columnSelector && !this._columnSelector.columns.length) {
            const allColumns = employeeDisplayProperties.map((type) => ({ type, disabled: false }));
            const filteredColumns = filterForPermissions((permission) => app.hasPermission(permission), allColumns);
            this._columnSelector.columns = filteredColumns;
        }

        if (this._filtersEl && !this._filtersEl.filters.length) {
            this._filtersEl.filters = [];
        }
    }

    private _allowsExportToDocuments() {
        return (
            [ExportFormat.PayrollReportDetailed, ExportFormat.TimeSheet].includes(this._page) &&
            app.hasPermission("manage.employees.documents")
        );
    }

    private _hasPermission(page: ExportFormat) {
        return canAccess(exportsRoute(exportsPage(page)), routeAccess());
    }

    private async _submit(e: Event) {
        e.preventDefault();
        this._exportResponse = null;
        this._loading = true;
        try {
            const exportPromise = app.api.export(new ExportParams({ ...this._exportFormData, format: this._page }));
            this._exportResponse = await exportPromise;
        } catch (error) {
            void alert(error.message, { type: "warning" });
        } finally {
            this._loading = false;
        }
    }

    static styles = [
        shared,
        DateInput.styles,
        Checkbox.styles,
        css`
            :host {
                display: flex;
            }

            .sub-menu {
                width: 20em;
            }

            .main {
                flex: 1;
                position: relative;
            }
        `,
    ];

    render() {
        if (!app.company) {
            return html``;
        }

        const rangeSuggestions: { from: string; to: string; label: string }[] = [];

        const pagesWithoutDateRange = [
            ExportFormat.PayrollAgendaEmployeeCoreData,
            ExportFormat.EmployeeDataCSV,
            ExportFormat.EmployeeDataPDF,
        ];

        let date = toDateString(new Date());

        for (let i = 0; i < 12; i++) {
            rangeSuggestions.push({
                label: formatMonthShort(date),
                ...getRange(date, "month"),
            });
            date = dateAdd(date, { months: -1 });
        }

        return html`
            <div class="sub-menu scrolling">
                <ptc-exports-menu
                    .page=${this._page}
                    @navigate=${(e: CustomEvent<ExportFormat>) => {
                        this.go(`exports/${exportsPage(e.detail)}`, {
                            ...this.dateRange!,
                        });
                    }}
                ></ptc-exports-menu>
            </div>

            <div class="main">
                <ptc-scroller class="fullbleed">
                    <div class="double-padded">
                        <div class="narrow-content">
                            <ptc-exports-headers .page=${this._page}></ptc-exports-headers>

                            <form
                                @submit=${this._submit}
                                @change=${() => this.requestUpdate()}
                                ?disabled=${this._loading || !!this._exportResponse}
                            >
                                <div class="horizontal spacing layout">
                                    ${this._page && !pagesWithoutDateRange.includes(this._page)
                                        ? html`
                                              <div class="field stretch">
                                                  <label>Zeitraum</label>
                                                  <ptc-date-range-picker
                                                      @range-selected=${(e: CustomEvent<DateRange>) =>
                                                          this.go(null, e.detail)}
                                                      .range=${this.dateRange}
                                                      class="box"
                                                  ></ptc-date-range-picker>
                                              </div>
                                          `
                                        : ""}
                                    ${[
                                        ExportFormat.AverageWorkTimeExcel,
                                        ExportFormat.BalancesTimeExcel,
                                        ExportFormat.BalancesVacationExcel,
                                        ExportFormat.BalancesBonusesExcel,
                                    ].includes(this._page)
                                        ? html`
                                              <div class="field">
                                                  <label>Auflösung</label>
                                                  <select class="slim" name="resolution">
                                                      <option
                                                          .value=${"week"}
                                                          ?selected=${this._resolutionParam === "week"}
                                                      >
                                                          Woche
                                                      </option>
                                                      <option
                                                          .value=${"month"}
                                                          ?selected=${this._resolutionParam === "month" ||
                                                          !this._resolutionParam}
                                                      >
                                                          Monat
                                                      </option>
                                                      <option
                                                          .value=${"year"}
                                                          ?selected=${this._resolutionParam === "year"}
                                                      >
                                                          Jahr
                                                      </option>
                                                  </select>
                                              </div>
                                          `
                                        : ""}
                                </div>
                                ${this._page &&
                                (this._page.startsWith("payroll") ||
                                    this._page.startsWith("employee_data") ||
                                    this._page.startsWith("balances") ||
                                    this._page === ExportFormat.TimeSheet ||
                                    this._page === ExportFormat.RosterExcel ||
                                    this._page.startsWith("employee_table_excel"))
                                    ? html`
                                          <div class="field">
                                              <label>Mitarbeiter</label>
                                              <div class="focus-container horizontal center-aligning layout">
                                                  ${this._page.startsWith("employee_data")
                                                      ? html`
                                                            <ptc-entity-filters
                                                                hideFilterIcon
                                                                .filterTypes=${[
                                                                    "employmentType",
                                                                    "venue",
                                                                    "department",
                                                                    "position",
                                                                    "employeeId",
                                                                    "employeeStatus",
                                                                    "employeeTag",
                                                                ] as const}
                                                                class="stretch"
                                                            ></ptc-entity-filters>
                                                        `
                                                      : html`
                                                            <ptc-entity-filters
                                                                hideFilterIcon
                                                                .filterTypes=${[
                                                                    "employmentType",
                                                                    "venue",
                                                                    "department",
                                                                    "position",
                                                                    "employeeId",
                                                                    "costCenter",
                                                                    "employeeTag",
                                                                ] as const}
                                                                class="stretch"
                                                            ></ptc-entity-filters>
                                                        `}
                                                  <ptc-column-selector
                                                      ?hidden=${!this._page.startsWith("employee_table_excel")}
                                                  ></ptc-column-selector>
                                                  <button
                                                      class="skinny transparent"
                                                      title="Sortierung"
                                                      style="margin-right: 0.25em;"
                                                      type="button"
                                                  >
                                                      <ptc-ui-icon name="icon.action.sort-descending"></ptc-ui-icon>
                                                  </button>
                                                  <ptc-popover>
                                                      <div class="horizontal spacing center-aligning layout">
                                                          <div>
                                                              <label>Sortieren nach...</label>
                                                              <select class="semibold small slim" name="sortProperty">
                                                                  <option value="lastName" selected>Nachname</option>
                                                                  <option value="firstName">Vorname</option>
                                                                  <option value="staffNumber">Personalnummer</option>
                                                                  <option value="birthday">Geburtstag</option>
                                                                  <option value="birthdayDate">
                                                                      Geburtstag (Datum)
                                                                  </option>
                                                                  <option value="email">Email</option>
                                                              </select>
                                                          </div>
                                                          <div>
                                                              <label>&nbsp;</label>
                                                              <select class="semibold small slim" name="sortDirection">
                                                                  <option value="ascending" selected>
                                                                      Aufsteigend
                                                                  </option>
                                                                  <option value="descending">Absteigend</option>
                                                              </select>
                                                          </div>
                                                      </div>
                                                  </ptc-popover>
                                              </div>
                                          </div>
                                      `
                                    : ""}
                                ${this._page && this._page.startsWith("cashbook")
                                    ? html`
                                          <div class="field">
                                              <label>Standort</label>
                                              <select name="venueId">
                                                  ${app.accessibleVenues.map(
                                                      (venue) => html`
                                                          <option .value=${venue.id.toString()}>${venue.name}</option>
                                                      `
                                                  )}
                                              </select>
                                          </div>
                                      `
                                    : ""}

                                <div class="field">
                                    <label>
                                        Versenden an
                                        <ptc-ui-icon name="icon.status.info" id="sendAsEmailInfo"></ptc-ui-icon>
                                        <ptc-ui-tooltip for="sendAsEmailInfo">
                                            Der Link zum Dokument ist für 14 Tage gültig.
                                        </ptc-ui-tooltip>
                                    </label>
                                    <input type="email" placeholder="Emailaddresse" name="sendAsEmail" />
                                </div>

                                ${this._allowsExportToDocuments()
                                    ? html`
                                          <div class="field">
                                              <ptc-checkbox-button
                                                  label="In der digitalen Personalakte ablegen"
                                                  buttonClass="semislim ghost"
                                                  name="saveToDocuments"
                                              ></ptc-checkbox-button>
                                          </div>

                                          ${this._page === ExportFormat.TimeSheet && app.company?.country === "DE"
                                              ? html`
                                                    <div class="field">
                                                        <ptc-checkbox-button
                                                            label="Pausenzeiten anzeigen"
                                                            buttonClass="semislim ghost"
                                                            name="showBreakDetails"
                                                        ></ptc-checkbox-button>
                                                    </div>
                                                `
                                              : ""}
                                          ${this._page === ExportFormat.TimeSheet
                                              ? html`
                                                    <div class="field" ?hidden=${!this._exportFormData.saveToDocuments}>
                                                        <ptc-checkbox-button
                                                            label="Unterschrift anfordern"
                                                            buttonClass="semislim ghost"
                                                            name="requireEmployeeSignature"
                                                        ></ptc-checkbox-button>
                                                    </div>
                                                `
                                              : ""}

                                          <div class="field" ?hidden=${!this._exportFormData.saveToDocuments}>
                                              <label>Kategorien</label>
                                              <ptc-document-tags-input
                                                  .availableTags=${this._availableTags || []}
                                              ></ptc-document-tags-input>
                                          </div>
                                      `
                                    : nothing}

                                <div class="double-margined vertical layout">
                                    <button class="primary">
                                        <ptc-ui-icon name="icon.action.download"></ptc-ui-icon> Export starten
                                    </button>
                                </div>
                            </form>

                            <ptc-exports-result
                                .scroller=${this._scroller}
                                ?loading=${this._loading}
                                .exportResponse=${this._exportResponse}
                                .onCancel=${() => {
                                    this._loading = false;
                                    this._exportResponse = null;
                                }}
                            ></ptc-exports-result>
                        </div>
                    </div>
                </ptc-scroller>
            </div>
        `;
    }
}
